// src/ecrans/orchestrateur/EcranAvisOrch.jsx — « LA PROPRETÉ, VUE PAR LES
// VOYAGEURS » (maquette v677, Orchestrateur › Avis, relevés 189 à 193).
//
// Lu SEULEMENT par `ecran_orch_avis_lire`. Seule la propreté et le commentaire,
// jamais une réponse ni une épingle. Une panne s'affiche, jamais « 0 avis ».
// « Le voyageur parle du ménage » attend le tri de l'opérateur : l'écran le dit.

import { useMemo, useState } from "react";
import { useLecturePorte } from "../portes-g3";
import { StyleG3, EtatG3, Feuille, dateCourte, MOIS, COULEURS as C } from "../nest-g3";
import { PAR_AGENT_MIN, bandeLogements, filtrer, groupesAvis, moisPresents, niveau, noteLue, parAgent, phraseControle } from "./avis-calculs";

const CSS = `
.ga-hd{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-start;justify-content:space-between;margin-bottom:12px}
.ga-hd h2{font-size:19px;font-weight:600;margin:0}
.ga-hd p{font-size:12px;color:${C.gris};margin:3px 0 0;max-width:560px}
.ga-fil{display:flex;gap:8px;flex-wrap:wrap}
.ga-logs{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:8px;margin-bottom:12px}
.ga-log{all:unset;box-sizing:border-box;cursor:pointer;background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:10px 12px;display:block}
.ga-log.on{box-shadow:inset 0 0 0 1.5px ${C.vert}}
.ga-log b{display:block;font-size:13px}
.ga-log .pl{font-size:10.5px;color:${C.gris};display:block}
.ga-log em{font-style:normal;font-size:20px;font-weight:700}
.ga-log em small{display:inline;font-size:11px;font-weight:500}
.ga-log em.ok{color:${C.vertF}}.ga-log em.gr{color:${C.gris}}.ga-log em.ab{color:${C.aub}}
.ga-bar{display:block;width:64px;height:4px;border-radius:2px;background:#EEF1EF}
.ga-bar i{display:block;height:4px;border-radius:2px;background:#98A2AD}
.ga-bar i.ok{background:${C.vert}}.ga-bar i.ab{background:${C.aub}}
.ga-ar{color:${C.aub}}
.ga-log small{display:block;font-size:10.5px;color:${C.gris}}
.ga-notes{display:flex;gap:14px;margin:6px 0}
.ga-T{font-size:13px;font-weight:600;margin:14px 0 7px}
.ga-c{display:inline-flex;min-width:20px;justify-content:center;border-radius:10px;font-size:11px;padding:0 6px;background:${C.aub};color:#fff;margin-left:4px}
.ga-c.gr{background:#98A2AD}
.ga-r{all:unset;box-sizing:border-box;cursor:pointer;display:block;width:100%;background:#fff;border:1px solid ${C.bord};border-radius:12px;padding:10px 12px;margin-bottom:6px}
.ga-top{display:flex;gap:10px;align-items:center}
.ga-note{font-size:15px;font-weight:700}
.ga-note small{font-size:10.5px;font-weight:500}
.ga-note.ok{color:${C.vertF}}.ga-note.gr{color:${C.gris}}.ga-note.ab{color:${C.aub}}
.ga-top b{display:block;font-size:12.5px}
.ga-top small{font-size:11px;color:${C.gris}}
.ga-r.ab{background:${C.aubP};border-color:${C.aubBord}}
.ga-q{display:block;font-size:12.5px;font-style:italic;margin:7px 0;padding-left:10px;border-left:2px solid #CDD4D1;quotes:none}
.ga-r.ab .ga-q{border-left-color:${C.aub}}
.ga-sans{display:block;font-size:11.5px;color:${C.grisD};font-style:italic;margin:6px 0}
.ga-mn{display:block;font-size:11px;color:${C.gris}}
.ga-mn i{font-style:normal;color:${C.aub}}
`;

const PLATEFORMES = [["tous", "Toutes"], ["airbnb", "Airbnb"], ["booking", "Booking"]];
const nomPlateforme = (p) => (p === "booking" ? "Booking" : "Airbnb");

function Note({ a }) {
  return (
    <em className={`ga-note ${niveau(a.note_proprete, a.sur) || "gr"}`} style={{ fontStyle: "normal" }}>
      {noteLue(a.note_proprete)}
      <small>/{a.sur}</small>
    </em>
  );
}

function NoteLogement({ nom, note, sur }) {
  const n = niveau(note, sur);
  return (
    <span>
      <span className="pl">{nom}</span>
      <em className={n}>
        {noteLue(note)}
        <small>/{sur}</small>
      </em>
      <span className="ga-bar">
        <i className={n} style={{ width: `${Math.min(100, (note / sur) * 100)}%` }} />
      </span>
    </span>
  );
}

function LigneMenage({ m }) {
  if (!m) return <span className="ga-mn">— · ménage introuvable</span>;
  const phrase = phraseControle(m.controle);
  return (
    <span className="ga-mn" data-testid="avis-menage">
      <b>{m.agent ? m.agent.prenom : "—"}</b> · ménage du {dateCourte(m.date)} ·{" "}
      {m.controle?.etat === "reprises" ? <i>{phrase}</i> : phrase}
    </span>
  );
}

function Carte({ a, onOuvrir }) {
  return (
    <button type="button" className={`ga-r${niveau(a.note_proprete, a.sur) === "ab" ? " ab" : ""}`} data-testid="avis-carte" onClick={() => onOuvrir(a)}>
      <span className="ga-top">
        <Note a={a} />
        <span>
          <b>{a.logement.nom}</b>
          <small>
            {dateCourte(a.date)} · {nomPlateforme(a.plateforme)}
          </small>
        </span>
      </span>
      {a.commentaire ? <q className="ga-q">{a.commentaire}</q> : <span className="ga-sans">Note seule, sans commentaire</span>}
      <LigneMenage m={a.menage} />
    </button>
  );
}

function FeuilleAvis({ a, onFermer }) {
  return (
    <Feuille titre={a.logement.nom} onFermer={onFermer} testId="avis-feuille">
      <div className="ga-top">
        <Note a={a} />
        <span>
          <b>Propreté</b>
          <small>
            {nomPlateforme(a.plateforme)} · {dateCourte(a.date)}
          </small>
        </span>
      </div>
      <p className="ga-T">Le commentaire du voyageur</p>
      {a.commentaire ? <q className="ga-q">{a.commentaire}</q> : <span className="ga-sans">Note seule, sans commentaire</span>}
      <p className="ga-T">Le ménage avant ce séjour</p>
      {a.menage ? (
        <p style={{ fontSize: 12, margin: 0 }}>
          <b>{a.menage.agent ? a.menage.agent.prenom : "—"}</b> le {dateCourte(a.menage.date)}
          <br />
          <small>{phraseControle(a.menage.controle)}</small>
        </p>
      ) : (
        <p className="g3-etat">— Ménage introuvable pour ce séjour.</p>
      )}
      <p className="g3-etat">Répondre au voyageur et afficher l'avis sur la page du logement : c'est l'admin.</p>
      <button type="button" className="g3-bt" onClick={onFermer}>
        Fermer
      </button>
    </Feuille>
  );
}

export default function EcranAvisOrch() {
  const [lecture, relire] = useLecturePorte("ecran_orch_avis_lire");
  const [mois, setMois] = useState("tout");
  const [plateforme, setPlateforme] = useState("tous");
  const [logement, setLogement] = useState("tous");
  const [ouvert, setOuvert] = useState(null);
  const data = lecture.etat === "ok" ? lecture.data : null;
  const rienALire = Boolean(data && (data.perimetre_vide || data.aucun_menage_equipe));
  const avis = useMemo(() => data?.avis || [], [data]);
  const listeMois = useMemo(() => moisPresents(avis), [avis]);
  const surPeriode = filtrer(avis, { mois, plateforme, logement: "tous" });
  const liste = filtrer(avis, { mois, plateforme, logement }).sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const bande = bandeLogements(data?.logements, surPeriode);
  const { sous, parle, autres } = groupesAvis(liste);
  const debut = listeMois.length ? MOIS[Number(listeMois[listeMois.length - 1].slice(5)) - 1] : null;
  return (
    <div className="g3" data-testid="ecran-avis-orch">
      <StyleG3 />
      <style>{CSS}</style>
      <div className="g3-page">
        <div className="ga-hd">
          <div>
            <h2>La propreté, vue par les voyageurs</h2>
            <p>Seule la propreté vous concerne, avec le commentaire écrit. Répondre et choisir les avis de la page : l'admin.</p>
          </div>
          {data ? (
            <div className="ga-fil">
              <div className="g3-seg">
                {[["tout", debut ? `Depuis ${debut}` : "Tout"], ...listeMois.slice(0, 2).map((m) => [m, cap(MOIS[Number(m.slice(5)) - 1])])].map(([id, t]) => (
                  <button key={id} type="button" className={mois === id ? "on" : ""} onClick={() => setMois(id)}>
                    {t}
                  </button>
                ))}
              </div>
              <div className="g3-seg">
                {PLATEFORMES.map(([id, t]) => (
                  <button key={id} type="button" className={plateforme === id ? "on" : ""} onClick={() => setPlateforme(id)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <EtatG3 lecture={lecture} quoi="Les avis" onRelire={relire} />
        {data ? (
          <>
            {data.perimetre_vide ? <p className="g3-manque">Vos logements n'ont pas pu être lus, ou aucun ne vous est confié.</p> : null}
            {data.aucun_menage_equipe ? (
              <p className="g3-manque" data-testid="avis-sans-menage">
                Aucun ménage terminé par votre équipe sur vos logements : il n'y a pas encore d'avis à regarder.
              </p>
            ) : null}
            {bande.length ? (
              <div className="ga-logs" data-testid="avis-bande">
                {bande.map((g) => (
                  <button key={g.id} type="button" className={`ga-log${logement === g.id ? " on" : ""}`} onClick={() => setLogement(logement === g.id ? "tous" : g.id)}>
                    <b>{g.nom}</b>
                    <span className="ga-notes">
                      {g.airbnb !== null ? <NoteLogement nom="Airbnb" note={g.airbnb} sur={5} /> : null}
                      {g.booking !== null ? <NoteLogement nom="Booking" note={g.booking} sur={10} /> : null}
                    </span>
                    <small className={g.aRegarder ? "ga-ar" : ""}>{g.aRegarder ? `${g.aRegarder} à regarder` : `${g.n} avis · rien à reprendre`}</small>
                    <small>{logement === g.id ? "filtre actif · retirer" : "voir ce logement"}</small>
                  </button>
                ))}
              </div>
            ) : null}
            {rienALire ? null : (
              <>
                <p className="ga-T">
                  Propreté sous le seuil <span className={`ga-c${sous.length ? "" : " gr"}`}>{sous.length}</span>
                </p>
                {sous.length ? sous.map((a) => <Carte key={a.id} a={a} onOuvrir={setOuvert} />) : <p className="g3-etat">Aucune propreté sous le seuil.</p>}
              </>
            )}
            {rienALire ? null : (
              <>
            <p className="ga-T" data-testid="avis-parle">
              Le voyageur parle du ménage{" "}
              <span className={`ga-c${parle.length ? "" : " gr"}`}>{data.tri_operateur ? parle.length : "—"}</span>
            </p>
            {data.tri_operateur ? (
              parle.map((a) => <Carte key={a.id} a={a} onOuvrir={setOuvert} />)
            ) : (
              <p className="g3-manque">— Le tri par l'opérateur n'est pas encore branché : aucun avis n'est classé ici.</p>
            )}
            <p className="ga-T">
              {data.tri_operateur ? "Rien à signaler" : "Les autres avis"} <span className="ga-c gr">{autres.length}</span>
            </p>
            {autres.map((a) => (
              <Carte key={a.id} a={a} onOuvrir={setOuvert} />
            ))}
            {liste.length < PAR_AGENT_MIN ? (
              <p className="g3-etat">« Par agent » apparaît à partir de {PAR_AGENT_MIN} séjours notés sur la période.</p>
            ) : (
              <details data-testid="avis-par-agent">
                <summary className="ga-T">Par agent</summary>
                <p className="g3-etat">Un indice, pas une note : la propreté dépend aussi du logement et du voyageur</p>
                {parAgent(liste).map((g) => (
                  <div className="ga-r" key={g.prenom} style={{ cursor: "default" }}>
                    <span className="ga-top">
                      <span>
                        <b>{g.prenom}</b>
                        <small>
                          {g.n} séjour{g.n > 1 ? "s" : ""} noté{g.n > 1 ? "s" : ""} · {g.sous ? `${g.sous} sous le seuil` : "aucun sous le seuil"} · {g.reprises} reprise{g.reprises > 1 ? "s" : ""} au contrôle
                        </small>
                      </span>
                    </span>
                  </div>
                ))}
              </details>
            )}
              </>
            )}
          </>
        ) : null}
      </div>
      {ouvert ? <FeuilleAvis a={ouvert} onFermer={() => setOuvert(null)} /> : null}
    </div>
  );
}

function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
